// Drag handle for resizing a table column. Sits absolute on the right edge of
// each <th>. Drag = update the width via the column-state hook.

import { useCallback, useRef } from 'react';
import { MIN_COLUMN_WIDTH } from '../lib/accountsPresets';

export interface AccountsColumnResizeHandleProps {
  columnKey: string;
  /** Current width in px (used as the starting point on mousedown). */
  currentWidth: number;
  onResize: (key: string, width: number) => void;
}

export function AccountsColumnResizeHandle({
  columnKey,
  currentWidth,
  onResize,
}: AccountsColumnResizeHandleProps) {
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const delta = e.clientX - startXRef.current;
      const next = Math.max(MIN_COLUMN_WIDTH, startWidthRef.current + delta);
      onResize(columnKey, next);
    },
    [columnKey, onResize],
  );

  const handleMouseUp = useCallback(() => {
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [handleMouseMove]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      startXRef.current = e.clientX;
      startWidthRef.current = currentWidth;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    },
    [currentWidth, handleMouseMove, handleMouseUp],
  );

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize column"
      onMouseDown={handleMouseDown}
      onClick={(e) => e.stopPropagation()}
      // Always-visible 1px guideline at the right edge + 4px hit area to the
      // left of it. Hovering reveals a stronger blue accent. z-10 lifts it
      // above sibling content (sort buttons, filter inputs).
      className="absolute top-0 right-0 z-10 flex h-full w-2 cursor-col-resize items-stretch justify-end select-none group"
    >
      <span className="block h-full w-px bg-border group-hover:bg-primary group-hover:w-0.5 transition-all" />
    </div>
  );
}
